import type { stack as it } from "../it/stack.ts";
import { formati } from "../formati.ts";

const f = formati("en");

/** Il dettaglio di uno stack: configurazione, verifica, rilascio, container, storico. */
export const stack: typeof it = {
  "verifica.impostaVariabile": "Set the variable",
  "verifica.mostraEnvironment": "Show in environment",
  "verifica.apriImmagine": "Open the image in the compose",
  "verifica.apriNelCompose": "Open in the compose",
  "verifica.conseguenza.MISSING_VARIABLE": "The compose would use it empty; if it's required (:?), the start would stop.",
  "verifica.conseguenza.MISSING_TAG": "You couldn't tell which version is running, or roll back.",
  "verifica.conseguenza.MISSING_IMAGE": "Portman starts stacks from images already in a registry.",
  "verifica.conseguenza.DUPLICATE_PORT": "Docker would refuse to start the second container.",
  "verifica.conseguenza.MUTABLE_TAG": "The image contents can change without notice.",
  "verifica.conseguenza.UNMANAGED_PORT": "The port would change on every start, and the service's address with it.",
  "verifica.conseguenza.UNUSED_VARIABLE": "No service reads it: it's probably a leftover.",
  "verifica.titolo.MISSING_VARIABLE": (p: { variabile: string }) => `The variable ${p.variabile} has no value`,
  "verifica.titolo.MISSING_TAG": (p: { servizio: string }) => `The image of "${p.servizio}" has no tag`,
  "verifica.titolo.MISSING_IMAGE": (p: { servizio: string }) => `The service "${p.servizio}" declares no image`,
  "verifica.titolo.DUPLICATE_PORT": "Two services request the same host port",
  "verifica.titolo.MUTABLE_TAG": (p: { servizio: string }) => `The service "${p.servizio}" uses a mutable tag`,
  "verifica.titolo.UNMANAGED_PORT": (p: { servizio: string }) =>
    `The service "${p.servizio}" lets Docker pick the port`,
  "verifica.titolo.UNUSED_VARIABLE": (p: { variabile: string }) =>
    `The variable ${p.variabile} isn't used by the compose`,
  "verifica.daRisolvere": "Fix before starting",
  "verifica.daValutare": "Worth a look",
  "verifica.bloccanti": (p: { n: number }) =>
    `${p.n} ${f.plurale(p.n, { one: "problem prevents", other: "problems prevent" })} the start`,
  "verifica.prontoDopoSalvataggio": "Ready, once saved",
  "verifica.pronto": "Ready to start",
  "verifica.siFermerebbe": "The start would stop before touching Docker.",
  "verifica.usaUltimaRevisione": "The start uses the last saved revision: save your fixes for them to count.",
  "verifica.avvisi": (p: { n: number }) =>
    `There ${f.plurale(p.n, { one: "is 1 warning", other: `are ${p.n} warnings` })}, which don't block the start.`,
  "verifica.superati": "Checks passed: variables, images and ports.",
  "verifica.ricontrolla": "Check again",
  "verifica.tuttiRisolti": "all fixed",
  "verifica.risolto": "Fixed, save to confirm.",
  "verifica.dettagli": "Details",
  "verifica.valoreNascosto": "hidden value",
  "verifica.mostraPerModificare": "show to edit",
  "verifica.segnaposto": "value",
  "verifica.segnapostoPredefinito": (p: { valore: string }) => `${p.valore} (default)`,
  "verifica.valoreDi": (p: { nome: string }) => `Value of ${p.nome}`,
  "verifica.nomiOccupati": "Container names already in use",
  "verifica.nomeOccupato": (p: { nome: string; servizio: string; stato: string; progetto: string | null }) =>
    `${p.nome} (service ${p.servizio}) is already used by a ${p.stato} container` +
    (p.progetto === null ? " started outside Compose" : ` in the Compose project "${p.progetto}"`) +
    ".",
  "verifica.nomiSpiegazione":
    "Names set with container_name apply to the whole Docker daemon, not just the project: " +
    "two stacks can't use the same one. Remove the containers holding them, or change the names in this stack.",
  "verifica.porteOccupate": "Ports already in use on the host",
  "verifica.portaOccupata": (p: { porta: number; da: string }) => `Port ${p.porta} is in use by ${p.da}.`,
  "verifica.portaLibera": (p: { porta: number }) => `Free: ${p.porta}.`,
  "verifica.interruzioneRicreazione": "about a second, while containers are recreated",
  "verifica.interruzioneBlueGreen": "none: traffic moves once the new version is ready",
  "verifica.cosaAccadra": "What happens on start",
  "verifica.immagini": "Images to pull",
  "verifica.porteAssegnate": "Ports assigned by Portman",
  "verifica.nessuna": "none",
  "verifica.interruzione": "Expected downtime",
  "verifica.daCorreggere": "to fix",
  "verifica.daValutareBreve": "worth a look",
  "verifica.nessunaImmagine": "no image",
  "verifica.soloInterna": "internal only",
  "verifica.assegnataDaPortman": "assigned by Portman",
  "verifica.auto": "auto",
  "verifica.senzaHealthcheckAiuto": "Without a healthcheck there's no way to wait for the service to be ready",
  "verifica.senzaHealthcheck": "no healthcheck",
  "verifica.statoImpostata": "set",
  "verifica.statoPredefinito": "default",
  "verifica.statoPortman": "by Portman",
  "verifica.statoMancante": "missing",
  "verifica.valoriEnvironment": "Environment values",
  "verifica.variabiliCompose": "Compose variables",
  "verifica.mancanti": (p: { n: number }) => `${p.n} missing`,
  "verifica.modificaliQui": "Edit them here: saving creates one revision with all changes together.",
  "verifica.daSalvare": "unsaved",
  "verifica.imposta": "Set",
  "verifica.modificate": (p: { n: number }) =>
    `${p.n} ${f.plurale(p.n, { one: "variable changed", other: "variables changed" })}`,
  "verifica.risolti": (p: { n: number }) =>
    `${p.n} ${f.plurale(p.n, { one: "problem fixed", other: "problems fixed" })}`,
  "verifica.salvareNonAvvia": "Saving creates a revision and checks the stack again. It doesn't start anything.",
  "verifica.salvaRicontrolla": "Save and check again",

  "container.filtroTutti": "All",
  "container.filtroAvvisi": "Warnings",
  "container.filtroErrori": "Errors",
  "container.live": "Live",
  "container.ogni5": "Every 5 s",
  "container.ogni10": "Every 10 s",
  "container.ogni30": "Every 30 s",
  "container.ogniMinuto": "Every minute",
  "container.inPausa": "Paused",
  "container.titolo": "Containers",
  "container.aggiorna": "Refresh",
  "container.vuoto": "No containers",
  "container.vuotoAiuto": "The stack hasn't been started on this host.",
  "container.logDi": (p: { nome: string }) => `Logs of ${p.nome}`,
  "container.mostraArrivate": "Show new lines",
  "container.righeNuove": (p: { n: number }) =>
    `${p.n} ${f.plurale(p.n, { one: "new line", other: "new lines" })}`,
  "container.selezione": "selecting",
  "container.inAscolto": "listening",
  "container.concluso": "stream ended",
  "container.aggiornamento": "Refreshing logs",
  "container.cercaSegnaposto": "Search logs…",
  "container.cerca": "Search logs",
  "container.nessunaRiga": "No log lines.",
  "container.nessunaCorrisponde": "No lines match.",

  "console.rilascio": "Deploy",
  "console.arresto": "Stop",
  "console.riavvio": "Restart",
  "console.pull": "Image pull",
  "console.rinomina": "Rename",
  "console.operazione": "Operation",
  "console.completato": "completed",
  "console.fallito": "failed",
  "console.inCorso": "in progress",
  "console.faseNeiLog": (p: { indice: number; totale: number; titolo: string; durata: string }) =>
    `step ${p.indice}/${p.totale} · ${p.titolo} (${p.durata})`,
  "console.fase": (p: { indice: number; totale: number; titolo: string }) =>
    `step ${p.indice} of ${p.totale} · ${p.titolo}`,
  "console.in": (p: { durata: string }) => `in ${p.durata}`,
  "console.riconnessione": "log connection lost, reattaching to the operation…",
  "console.annullo": "Cancelling…",
  "console.riduci": "Collapse",
  "console.mostraLog": (p: { righe: number }) => `Show logs${p.righe ? ` (${p.righe})` : ""}`,
  "console.attesaRighe": "waiting for the first lines…",

  "schede.panoramica": "overview",
  "schede.risorse": "resources",
  "schede.configurazione": "configuration",
  "schede.rilascio": "deploy",
  "schede.storico": "history",
  "striscia.inEsecuzione": "Running",
  "striscia.blueGreen": "Blue/green",
  "striscia.ricreazione": "Recreate · ~1 s downtime",
  "striscia.sani": (p: { sani: number; totali: number }) => `${p.sani}/${p.totali} ok`,
  "striscia.fermo": "stopped",
  "panoramica.vuoto": "The stack hasn't been started yet",
  "panoramica.vuotoAiuto":
    "Containers, logs and usage will show up here after the first start. Check the configuration first.",
  "panoramica.vaiConfigurazione": "Go to configuration",
  "dettaglio.generazioneAttiva": (p: { slot: string }) => `${p.slot} generation active`,
  "dettaglio.altreAzioni": "More actions",
  "dettaglio.riavvia": "Restart",
  "dettaglio.rilascia": (p: { n: number }) => `Deploy rev. ${p.n}`,
  "dettaglio.riprova": (p: { n: number }) => `Retry rev. ${p.n}`,
  "dettaglio.modificaDescrizione": "Edit description",
  "dettaglio.aggiungiDescrizione": "Add a description",
  "dettaglio.descrizioneSegnaposto": "What is this stack for?",
  "dettaglio.descrizione": "Stack description",
  "dettaglio.rinominaPrompt": (p: { nome: string; avviato: boolean }) =>
    `New name for "${p.nome}".` +
    (p.avviato ? " The stack will be stopped, its volumes copied, then restarted: expect downtime." : ""),
  "dettaglio.rinomina": "Rename",
  "dettaglio.duplicaPrompt": (p: { nome: string }) =>
    `Name for the copy of "${p.nome}". Domains and ports aren't copied.`,
  "dettaglio.duplicaNome": (p: { nome: string }) => `${p.nome}-copy`,
  "dettaglio.duplica": "Duplicate",
  "dettaglio.fermaPrima": "Stop the stack before deleting it",
  "dettaglio.eliminaConferma": (p: { nome: string }) =>
    `Delete "${p.nome}"? Configuration, revisions and history will be lost. Docker volumes are kept.`,
  "dettaglio.ferma": "Stop",
  "dettaglio.avvia": "Start",
  "dettaglio.operazioneInCorso": (p: { operazione: string; attore: string | null }) =>
    p.operazione +
    (p.attore === null ? "" : p.attore === "webhook" ? " from the webhook" : ` by ${p.attore}`) +
    " in progress: wait for it to finish.",
  "dettaglio.aiutoEditor": "Tab indents · Enter keeps indentation",
  "dettaglio.salvaRevisione": "Save revision",
  "dettaglio.nessunaModifica": "No changes",
  "dettaglio.sensibiliNascosti": "Sensitive values hidden",
  "dettaglio.mostraPerModificare": "Show to edit",
  "dettaglio.messaggioRevisione": "Version name (optional)",
  "dettaglio.annullaModifiche": "Discard changes",
  "dettaglio.nonSalvate": "Unsaved changes",

  "storico.ripristinareTitolo": (p: { numero: number }) => `Restore revision #${p.numero}?`,
  "storico.ripristinareTesto":
    "A new revision will be saved with its contents. With “Restore only” the stack keeps running " +
    "with the current configuration: the restored revision won't be active until you restart it.",
  "storico.soloRipristino": "Restore only",
  "storico.ripristinaAvvia": "Restore and start",
  "storico.revisioni": "Saved revisions",
  "storico.nessunaRevisione": "No revisions",
  "storico.senzaDescrizione": "no description",
  "storico.ripristina": "Restore",
  "storico.operazioni": "Past operations",
  "storico.nessunaOperazione": "No operations",
  "storico.rev": (p: { n: number }) => `rev. ${p.n}`,
  "storico.esitoRiuscita": "✓ succeeded",
  "storico.esitoFallita": "✗ failed",
  "storico.esitoAnnullata": "cancelled",
  "storico.esitoInCorso": "in progress",
  "storico.nessunLog": "No logs recorded.",
  "storico.cosaCambia": (p: { numero: number }) => `Changes since #${p.numero}`,
  "storico.completo": "Full contents",
  "storico.nessunaVariabile": "No variables.",
  "storico.invariato": "Unchanged.",
  "storico.compose": "Compose",
  "storico.environment": "Environment",
  "storico.righeInvariate": (p: { n: number }) =>
    `${p.n} ${f.plurale(p.n, { one: "unchanged line", other: "unchanged lines" })}`,

  "nuovo.composeEsempio":
    "services:\n" +
    "  web:\n" +
    "    image: nginx:${TAG}\n" +
    "    ports:\n" +
    "      # Write the host port as a variable: Portman assigns it,\n" +
    "      # and it stays the same on every new deploy.\n" +
    '      - "${PORT_WEB}:80"\n' +
    "    restart: unless-stopped\n",
  "nuovo.messaggio": "stack created",
  "nuovo.intro": "You can save an incomplete configuration too: checks run on start.",
  "nuovo.nome": "Name",
  "nuovo.nomeAiuto": "Lowercase letters, digits, hyphens. It becomes the Docker Compose project name.",
  "nuovo.nomeSegnaposto": "my-service",
  "nuovo.descrizione": "Description (optional)",
  "nuovo.titoloEditor": "New stack · docker-compose.yml",
  "nuovo.crea": "Create stack",
};
